import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@/hooks/useUser'
import { useLanguage } from '@/hooks/useLanguage'
import { useLoading } from '@/store/contexts/LoadingContext'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { t } from '@/utils/i18n'
import { getGuestId } from '@/utils/storage'
import { getLoginErrorMessage } from '@/utils/loginErrorUtils'
import { isWebView } from '@/utils/inAppPurchase'
import { userApi } from '@/services/api/user.api'
import type { AdRewardStatusResDto } from '@/store/types/point.types'
import { watchRewardedAdAndUpgradePro, isRewardedAdFlowAvailable } from '@/services/rewardedAdService'
import PageHeader from '@/components/common/PageHeader/PageHeader'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import {
  IconUser,
  IconEdit,
  IconCrown,
  IconClock,
  IconChevronRight,
  IconShield,
  IconFileText,
  IconGlobe,
  IconLoader,
  IconLink
} from '@/components/icons/Icons'
import './UserManagement.css'

function UserManagement() {
  const navigate = useNavigate()
  const { language, toggleLanguage } = useLanguage()
  const { showToast } = useToastContext()
  const { confirm } = useConfirmContext()
  const {
    currentUser,
    isAuthenticated,
    loading,
    updateUser,
    logout,
    loginWithGoogle,
    loginWithApple,
    fetchCurrentUser,
    deleteUser,
  } = useUser()
  const { startLoading, stopLoading } = useLoading()

  const [editNameModalOpen, setEditNameModalOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [isWatchingAd, setIsWatchingAd] = useState(false)
  const [adRewardStatus, setAdRewardStatus] = useState<AdRewardStatusResDto | null>(null)
  
  const hasLoadedDetailRef = useRef(false)
  const switchLinkBusyRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) {
      hasLoadedDetailRef.current = false
      return
    }
    if (hasLoadedDetailRef.current) return
    hasLoadedDetailRef.current = true
    
    const load = async () => {
      startLoading()
      try {
        await fetchCurrentUser({ silent: true })
        const adStatus = await userApi.getAdRewardStatus()
        setAdRewardStatus(adStatus)
      } catch (err) {
        console.warn('사용자 정보 조회 실패:', err)
      } finally {
        stopLoading()
      }
    }
    load()
  }, [isAuthenticated])

  const handleUpdateName = async () => {
    if (!editName.trim()) return
    try {
      await updateUser({
        name: editName.trim(),
        email: currentUser?.email || '',
      })
      showToast(t('messages.updateSuccess', language), 'success')
      setEditNameModalOpen(false)
    } catch (err) {
      showToast(t('messages.updateFailed', language), 'error')
    }
  }

  const handleLogout = async () => {
    const confirmed = await confirm({
      message: t('userManagement.logoutConfirm', language) || '로그아웃 하시겠습니까?',
    })
    if (confirmed) {
      await logout()
      navigate('/login')
    }
  }

  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      message: t('settings.deleteAccountConfirm', language) || '정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
      variant: 'danger'
    })
    
    if (confirmed) {
      try {
        await deleteUser()
        showToast(t('messages.deleteSuccess', language) || '계정이 삭제되었습니다.', 'success')
        navigate('/login')
      } catch (error) {
        showToast(t('messages.deleteFailed', language) || '계정 삭제에 실패했습니다.', 'error')
      }
    }
  }

  const handleWatchAdToExtendPro = async () => {
    if (!isRewardedAdFlowAvailable()) return
    setIsWatchingAd(true)
    try {
      const result = await watchRewardedAdAndUpgradePro()
      if (result.success) {
        showToast(language === 'ko' ? 'PRO 기간이 연장되었습니다.' : 'PRO period extended.', 'success')
        await fetchCurrentUser({ silent: true })
        const adStatus = await userApi.getAdRewardStatus()
        setAdRewardStatus(adStatus)
      } else {
        showToast(result.message || 'Error', 'error')
      }
    } catch {
      showToast('Error', 'error')
    } finally {
      setIsWatchingAd(false)
    }
  }

  const isGuestUser = (): boolean => {
    if (!currentUser) return false
    const role = currentUser.role?.toUpperCase()
    return role === 'GUEST' || role === 'ROLE_GUEST' || (currentUser.email?.includes('guest'))
  }

  const handleLinkAccount = async (provider: 'google' | 'apple') => {
    if (switchLinkBusyRef.current) return
    switchLinkBusyRef.current = true
    const guestId = getGuestId() ?? undefined
    try {
      if (provider === 'google') await loginWithGoogle(guestId)
      else await loginWithApple(guestId)
      await fetchCurrentUser({ silent: true })
    } catch (err: any) {
      const message = getLoginErrorMessage(err, language === 'ko' ? '연동 실패' : 'Link failed', true)
      showToast(message, 'error')
    } finally {
      switchLinkBusyRef.current = false
    }
  }

  const formatProExpireDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return d.toLocaleString(language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isPro = currentUser?.role === 'PRO' || currentUser?.role === 'ADMIN'

  if (!isAuthenticated && !loading) return null

  return (
    <div className="user-management">
      <PageHeader title={language === 'ko' ? '사용자 정보' : 'Account'} showBack={false} centerTitle titleVariant="user" />
      
      <div className="user-management__content">
        {/* 프로필 카드 */}
        <div className="user-management__profile-card">
          <div className="user-management__profile-header">
            <div className="user-management__avatar">
              {currentUser?.username ? currentUser.username.charAt(0).toUpperCase() : <IconUser size={32} />}
            </div>
            <div className="user-management__user-info">
              <div className="user-management__name-row">
                <div className="user-management__name" role="heading" aria-level={2}>
                  {currentUser ? currentUser.username : <Skeleton width={100} height={24} />}
                </div>
                {currentUser && (
                  <button className="user-management__edit-btn" onClick={() => { setEditName(currentUser.username || ''); setEditNameModalOpen(true); }}>
                    <IconEdit size={18} />
                  </button>
                )}
              </div>
              <div className="user-management__email">
                {currentUser ? currentUser.email : <Skeleton width={150} height={16} />}
              </div>
            </div>
          </div>

          <div className="user-management__badge-container">
            <div className={`user-management__role-badge ${isPro ? 'user-management__role-badge--pro' : ''}`}>
              <IconCrown size={18} />
              <span>{isPro ? 'PRO' : 'NORMAL'}</span>
            </div>
            {currentUser?.proExpireDate && (
              <div className="user-management__expire-date">
                <IconClock size={14} />
                <span>{formatProExpireDate(currentUser.proExpireDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* PRO 업그레이드 카드 */}
        {!isPro && (
          <div className="user-management__upgrade-card">
            <h3 className="user-management__upgrade-title">{t('myPage.upgradeToPro', language) || 'PRO로 업그레이드'}</h3>
            <p className="user-management__upgrade-desc">
              {t('myPage.upgradeDescription', language) || '모든 기능을 무제한으로 이용해보세요.'}
            </p>
            
            <div className="user-management__progress-container">
              <div className="user-management__progress-label">
                <span>{language === 'ko' ? '오늘 시청' : 'Today'}</span>
                <span>{adRewardStatus?.todayAdCount ?? 0} / 10</span>
              </div>
              <div className="user-management__progress-bar">
                <div className="user-management__progress-fill" style={{ width: `${(adRewardStatus?.todayAdCount ?? 0) * 10}%` }} />
              </div>
            </div>

            <button 
              className="user-management__upgrade-btn" 
              onClick={handleWatchAdToExtendPro}
              disabled={isWatchingAd || (adRewardStatus?.remainingCount === 0)}
            >
              {isWatchingAd ? <IconLoader className="animate-spin" size={20} /> : <><IconCrown size={18} /> {language === 'ko' ? '광고 시청하고 PRO 연장' : 'Watch Ad & Extend PRO'}</>}
            </button>
          </div>
        )}

        {/* 게스트 연동 카드 */}
        {isGuestUser() && (
          <div className="user-management__guest-card">
            <div className="user-management__guest-title-row">
              <IconLink size={20} />
              <h3 className="user-management__guest-title">{t('userManagement.linkAccount', language)}</h3>
            </div>
            <p className="user-management__guest-desc">{t('userManagement.linkAccountDescription', language)}</p>
            <div className="user-management__social-btns">
              <button className="user-management__social-btn user-management__social-btn--google" onClick={() => handleLinkAccount('google')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {t('userManagement.linkWithGoogle', language)}
              </button>
              {isWebView() && (
                <button className="user-management__social-btn user-management__social-btn--apple" onClick={() => handleLinkAccount('apple')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {t('userManagement.linkWithApple', language)}
                </button>
              )}
            </div>
          </div>
        )}

        {/* 설정 메뉴 */}
        <div className="user-management__menu-section">
          <h3 className="user-management__menu-title">{t('myPage.settings', language) || '설정'}</h3>
          <div className="user-management__menu-card">
            <button className="user-management__menu-item" onClick={() => navigate('/settings/privacy')}>
              <div className="user-management__menu-item-left">
                <div className="user-management__icon-box" style={{ background: '#eff6ff', color: '#3b82f6' }}><IconShield size={18} /></div>
                <span className="user-management__menu-label">{t('settings.privacy', language)}</span>
              </div>
              <IconChevronRight size={18} />
            </button>
            <div className="user-management__divider" />
            <button className="user-management__menu-item" onClick={() => navigate('/settings/terms')}>
              <div className="user-management__menu-item-left">
                <div className="user-management__icon-box" style={{ background: '#eef2ff', color: '#6366f1' }}><IconFileText size={18} /></div>
                <span className="user-management__menu-label">{t('settings.terms', language)}</span>
              </div>
              <IconChevronRight size={18} />
            </button>
            <div className="user-management__divider" />
            <button className="user-management__menu-item" onClick={toggleLanguage}>
              <div className="user-management__menu-item-left">
                <div className="user-management__icon-box" style={{ background: '#fdf2f8', color: '#ec4899' }}><IconGlobe size={18} /></div>
                <span className="user-management__menu-label">{language === 'ko' ? '언어 설정 (한국어)' : 'Language (English)'}</span>
              </div>
              <IconChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* 하단 액션 */}
        <div className="user-management__footer">
          <div className="user-management__footer-actions">
            <button className="user-management__footer-btn" onClick={handleLogout}>{t('userManagement.logoutButton', language)}</button>
            <span className="user-management__footer-sep">|</span>
            <button className="user-management__footer-btn user-management__footer-btn--danger" onClick={handleDeleteAccount}>{t('settings.deleteAccount', language)}</button>
          </div>
        </div>
      </div>

      {/* 이름 수정 모달 */}
      {editNameModalOpen && (
        <div className="user-management__modal-overlay">
          <div className="user-management__modal">
            <h3 className="user-management__modal-title">{t('userManagement.editName', language) || '이름 수정'}</h3>
            <input 
              className="user-management__modal-input" 
              type="text" 
              value={editName} 
              onChange={(e) => setEditName(e.target.value)} 
              autoFocus 
            />
            <div className="user-management__modal-buttons">
              <button className="user-management__modal-btn user-management__modal-btn--cancel" onClick={() => setEditNameModalOpen(false)}>{t('common.cancel', language)}</button>
              <button className="user-management__modal-btn user-management__modal-btn--save" onClick={handleUpdateName}>{t('common.save', language)}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
